/**
 * Store pub-sub maison Nestor (cf. ADR 0005).
 *
 * Ce module est volontairement minimal et auditable :
 *  - signature compatible avec `zustand/vanilla` pour faciliter une bascule
 *    si jamais on en a besoin (ADR 0005 § Conséquences) ;
 *  - aucune dépendance npm ;
 *  - aucun couplage à un framework UI — l'UI lit-html s'abonne via
 *    `subscribe(listener)` puis re-render manuellement.
 *
 * **Toute évolution de ce fichier nécessite un nouvel ADR ou un amendement
 * de l'ADR 0005** (cf. CLAUDE.md §3.6).
 */

export type Listener<T> = (state: T, previousState: T) => void;

export type Updater<T> = (state: T) => Partial<T> | T;

export interface Store<T> {
  getState(): T;
  setState(partial: Partial<T> | Updater<T>, replace?: boolean): void;
  subscribe(listener: Listener<T>): () => void;
}

/**
 * Crée un store réactif à partir d'un état initial.
 *
 * - `setState(partial)` merge un objet partiel dans l'état (shallow merge).
 * - `setState(updater)` reçoit l'état courant et retourne le delta.
 * - `setState(state, true)` remplace complètement l'état (utile pour les
 *   hydrations IndexedDB en Phase 2).
 * - `subscribe(listener)` retourne une fonction d'unsubscribe.
 *
 * Comparaison via `Object.is` : si la nouvelle valeur est strictement
 * identique à l'ancienne, les listeners ne sont pas notifiés (no-op).
 */
export function createStore<T extends object>(initial: T): Store<T> {
  let state: T = initial;
  const listeners = new Set<Listener<T>>();

  const getState = (): T => state;

  const setState = (partial: Partial<T> | Updater<T>, replace = false): void => {
    const delta = typeof partial === 'function' ? partial(state) : partial;
    if (Object.is(delta, state)) return;

    const previousState = state;
    state = replace ? (delta as T) : { ...state, ...delta };

    if (Object.is(state, previousState)) return;
    listeners.forEach((listener) => {
      listener(state, previousState);
    });
  };

  const subscribe = (listener: Listener<T>): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  return { getState, setState, subscribe };
}
